/**
 * Signing up without an invitation (docs/PERMISSIONS.md → "Signing up").
 *
 * No account is made when the form is sent — only a pending sign-up, and a
 * confirmation link to the address. The account exists once the link is
 * clicked, so typing someone else's address takes nothing from them: they can
 * still sign up with it, or be invited at it, whatever a stranger typed.
 * Where they land (founding Admin, invited role, a trusted domain's role, or
 * Requested) is `joinRoleFor` in auto-join.ts.
 */
import { z } from 'zod'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from './invitation.ts'
import { namePart } from './name.ts'

/** What the Sign up form sends. Validated on the server, whatever the browser checked. */
export const openSignupInputSchema = z.object({
  // Trimmed and lower-cased BEFORE it is checked, so `  Vic@Example.COM ` is
  // accepted as — and matched against trusted domains as — vic@example.com.
  email: z.string().trim().toLowerCase().pipe(z.email('Enter a valid email address.')),
  firstName: namePart('Enter your first name.'),
  lastName: namePart('Enter your last name.'),
  password: z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
    .max(PASSWORD_MAX_LENGTH, `Use ${PASSWORD_MAX_LENGTH} characters or fewer.`),
})
export type OpenSignupInput = z.infer<typeof openSignupInputSchema>

export const openSignupResultSchema = z.discriminatedUnion('outcome', [
  /** A confirmation link went to the address. Nothing else exists yet. */
  z.object({ outcome: z.literal('verify-email'), email: z.email() }),
  /** The address already belongs to a real account: sign in instead. */
  z.object({ outcome: z.literal('account-exists'), email: z.email() }),
])
export type OpenSignupResult = z.infer<typeof openSignupResultSchema>
